import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getEvents, deleteEvent, getCurrentUser } from '../../services/api';
import { Link } from 'react-router-dom';
import QRCodeDisplay from '../../components/shared/QRCodeDisplay';
import RegistrationsModal from '../../components/organizer/RegistrationsModal';

const ManageEvents = () => {
    const queryClient = useQueryClient();
    const userId = localStorage.getItem('userId');
    const [qrModalOpen, setQrModalOpen] = useState(false);
    const [regsModalOpen, setRegsModalOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);

    // Obtener datos del organizador actual (para el slug)
    const { data: currentUser } = useQuery({
        queryKey: ['currentUser'],
        queryFn: getCurrentUser
    });

    const { data, isLoading, error } = useQuery({
        queryKey: ['my-events', userId],
        queryFn: () => getEvents({ organizerId: userId }),
        refetchInterval: 5000 // Auto-refresh every 5 seconds
    });

    if (error) {
        console.error('Error fetching events:', error);
    }

    const events = Array.isArray(data) ? data : (data?.events || []);

    const deleteMutation = useMutation({
        mutationFn: (id) => deleteEvent(id),
        onSuccess: () => {
            queryClient.invalidateQueries(['my-events']);
            alert('Evento cancelado con éxito');
        }
    });

    const getStatusStyle = (status) => {
        switch (status) {
            case 'published': return 'bg-green-100 text-green-700';
            case 'in_progress': return 'bg-blue-100 text-blue-700';
            case 'finished': return 'bg-gray-100 text-gray-700';
            case 'cancelled': return 'bg-red-100 text-red-700';
            default: return 'bg-gray-100 text-gray-700';
        }
    };

    const handleOpenQrModal = (event) => {
        setSelectedEvent(event);
        setQrModalOpen(true);
    };

    const handleCloseQrModal = () => {
        setQrModalOpen(false);
        setSelectedEvent(null);
    };

    const handleOpenRegsModal = (event) => {
        setSelectedEvent(event);
        setRegsModalOpen(true);
    };

    const handleCloseRegsModal = () => {
        setRegsModalOpen(false);
        setSelectedEvent(null);
    };

    // Generar URL del microsite del evento
    const getEventWhitelabelUrl = (eventId) => {
        const baseUrl = window.location.origin;
        const slug = currentUser?.slug || 'mi-organizador';
        return `${baseUrl}/o/${slug}/events/${eventId}`;
    };

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-black text-gray-900 dark:text-white mb-1 sm:mb-2">Mis Pozos</h1>
                    <p className="text-gray-500 dark:text-gray-400 text-sm sm:text-base">Gestiona tus eventos, inscripciones y resultados.</p>
                </div>
                <Link
                    to="/events/create"
                    className="bg-primary-600 hover:bg-primary-700 text-white font-bold py-3 px-6 rounded-xl shadow-lg transition-all transform hover:-translate-y-1 flex items-center justify-center gap-2 text-sm sm:text-base"
                >
                    <span>+</span> Crear Nuevo Pozo
                </Link>
            </div>

            {/* Vista Desktop: Tabla */}
            <div className="hidden md:block bg-white dark:bg-slate-800 rounded-2xl lg:rounded-[2.5rem] border border-gray-100 dark:border-slate-700 shadow-sm overflow-hidden transition-colors">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-gray-50 dark:bg-slate-700 text-gray-400 dark:text-gray-500 text-xs font-black uppercase tracking-widest">
                            <tr>
                                <th className="px-6 py-4">Pozo / Club</th>
                                <th className="px-6 py-4">Fecha</th>
                                <th className="px-6 py-4">Inscritos</th>
                                <th className="px-6 py-4">Estado</th>
                                <th className="px-6 py-4 text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50 dark:divide-slate-700">
                            {isLoading ? (
                                [1, 2, 3].map(i => (
                                    <tr key={i} className="animate-pulse">
                                        <td colSpan="5" className="px-6 py-10 bg-gray-50/30 dark:bg-slate-700/30"></td>
                                    </tr>
                                ))
                            ) : error ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-20 text-center text-red-500 font-bold">
                                        Error al cargar los pozos: {error.message}
                                    </td>
                                </tr>
                            ) : events.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-20 text-center text-gray-500 font-bold">
                                        No has creado ningún pozo todavía.
                                    </td>
                                </tr>
                            ) : (
                                events.map((event) => (
                                    <tr key={event.id} className="hover:bg-gray-50/50 dark:hover:bg-slate-700/30 transition-colors">
                                        <td className="px-6 py-5">
                                            <div className="font-black text-gray-900 dark:text-white uppercase tracking-tight">{event.title}</div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400 font-bold">{event.club?.name}</div>
                                        </td>
                                        <td className="px-6 py-5">
                                            <div className="text-sm font-bold text-gray-700 dark:text-gray-300">{event.date}</div>
                                            <div className="text-xs text-gray-400 dark:text-gray-500">
                                                {typeof event.startTime === 'string' ? event.startTime : 'En curso'}h
                                            </div>
                                        </td>
                                        <td className="px-6 py-5">
                                            <div className="flex items-center gap-2">
                                                <div className="w-20 h-2 bg-gray-100 dark:bg-slate-700 rounded-full overflow-hidden">
                                                    <div
                                                        className="h-full bg-primary-500 rounded-full"
                                                        style={{ width: `${(event.currentPairs / (event.maxPairs * 2)) * 100}%` }}
                                                    ></div>
                                                </div>
                                                <span className="text-xs font-black text-gray-600 dark:text-gray-300">
                                                    {event.currentPairs}/{event.maxPairs * 2}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-5">
                                            <span className={`text-[10px] font-black uppercase px-2.5 py-1 rounded-lg ${getStatusStyle(event.status || 'published')}`}>
                                                {(event.status || 'published') === 'published' ? 'Abierto' : event.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-5 text-right space-x-2">
                                            <button
                                                onClick={() => handleOpenRegsModal(event)}
                                                className="inline-flex items-center justify-center w-10 h-10 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 rounded-xl hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-all"
                                                title="Lista de Inscritos"
                                            >
                                                👥
                                            </button>
                                            <button
                                                onClick={() => handleOpenQrModal(event)}
                                                className="inline-flex items-center justify-center w-10 h-10 bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 rounded-xl hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-all"
                                                title="QR y Enlace"
                                            >
                                                📱
                                            </button>
                                            <Link
                                                to={`/events/edit/${event.id}`}
                                                className="inline-flex items-center justify-center w-10 h-10 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-xl hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-all"
                                                title="Editar Evento"
                                            >
                                                ✏️
                                            </Link>
                                            <Link
                                                to={`/events/${event.id}/live`}
                                                className="inline-flex items-center justify-center w-10 h-10 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-600 transition-all"
                                                title="Gestionar en vivo"
                                            >
                                                📺
                                            </Link>
                                            <button
                                                onClick={() => { if (window.confirm('¿Anular este evento?')) deleteMutation.mutate(event.id) }}
                                                className="inline-flex items-center justify-center w-10 h-10 bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400 rounded-xl hover:bg-red-100 dark:hover:bg-red-900/30 transition-all"
                                                title="Cancelar"
                                            >
                                                🗑️
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Vista Mobile: Cards */}
            <div className="md:hidden space-y-4">
                {isLoading ? (
                    [1, 2, 3].map(i => (
                        <div key={i} className="bg-white rounded-2xl p-4 animate-pulse h-32"></div>
                    ))
                ) : error ? (
                    <div className="bg-white rounded-2xl p-6 text-center text-red-500 font-bold">
                        Error al cargar los pozos: {error.message}
                    </div>
                ) : events.length === 0 ? (
                    <div className="bg-white rounded-2xl p-8 text-center text-gray-500 font-bold">
                        No has creado ningún pozo todavía.
                    </div>
                ) : (
                    events.map((event) => (
                        <div key={event.id} className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm p-4 transition-colors">
                            <div className="flex justify-between items-start mb-3">
                                <div className="flex-1 min-w-0">
                                    <h3 className="font-black text-gray-900 dark:text-white uppercase tracking-tight truncate">{event.title}</h3>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 font-bold">{event.club?.name}</p>
                                </div>
                                <span className={`text-[10px] font-black uppercase px-2 py-1 rounded-lg ml-2 ${getStatusStyle(event.status || 'published')}`}>
                                    {(event.status || 'published') === 'published' ? 'Abierto' : event.status}
                                </span>
                            </div>

                            <div className="flex items-center gap-4 mb-4 text-sm">
                                <div className="text-gray-600 dark:text-gray-300">
                                    <span className="font-bold">{event.date}</span>
                                    <span className="text-gray-400 dark:text-gray-500 ml-1">
                                        {typeof event.startTime === 'string' ? event.startTime : 'En curso'}h
                                    </span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-16 h-2 bg-gray-100 dark:bg-slate-700 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-primary-500 rounded-full"
                                            style={{ width: `${(event.currentPairs / (event.maxPairs * 2)) * 100}%` }}
                                        ></div>
                                    </div>
                                    <span className="text-xs font-bold text-gray-600 dark:text-gray-300">
                                        {event.currentPairs}/{event.maxPairs * 2}
                                    </span>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-2 border-t border-gray-100 dark:border-slate-700 pt-3">
                                <button
                                    onClick={() => handleOpenRegsModal(event)}
                                    className="flex items-center justify-center gap-2 py-3 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 rounded-xl hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-all text-[11px] font-black uppercase tracking-widest"
                                >
                                    <span>👥</span> Inscritos
                                </button>
                                <button
                                    onClick={() => handleOpenQrModal(event)}
                                    className="flex items-center justify-center gap-2 py-3 bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 rounded-xl hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-all text-[11px] font-black uppercase tracking-widest"
                                >
                                    <span>📱</span> QR Pozo
                                </button>
                                <Link
                                    to={`/events/edit/${event.id}`}
                                    className="flex items-center justify-center gap-2 py-3 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-xl hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-all text-[11px] font-black uppercase tracking-widest"
                                >
                                    <span>✏️</span> Editar
                                </Link>
                                <Link
                                    to={`/events/${event.id}/live`}
                                    className="flex items-center justify-center gap-2 py-3 bg-slate-900 dark:bg-slate-700 text-white rounded-xl hover:bg-slate-800 dark:hover:bg-slate-600 transition-all text-[11px] font-black uppercase tracking-widest"
                                >
                                    <span>📺</span> Gestión
                                </Link>
                                <button
                                    onClick={() => { if (window.confirm('¿Anular este evento?')) deleteMutation.mutate(event.id) }}
                                    className="col-span-2 flex items-center justify-center gap-2 py-2 bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400 rounded-xl hover:bg-red-100 dark:hover:bg-red-900/30 transition-all text-[10px] font-bold uppercase tracking-widest border border-red-100/50 dark:border-red-900/30"
                                >
                                    <span>🗑️</span> Cancelar Pozo
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Modal QR */}
            {qrModalOpen && selectedEvent && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200 transaction-colors">
                        <div className="p-6 border-b border-gray-100 dark:border-slate-700 flex justify-between items-center">
                            <h2 className="text-xl font-black text-gray-900 dark:text-white">Compartir Evento</h2>
                            <button
                                onClick={handleCloseQrModal}
                                className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 flex items-center justify-center text-gray-500 dark:text-gray-400 transition-colors"
                            >
                                ✕
                            </button>
                        </div>
                        <div className="p-6">
                            <QRCodeDisplay
                                url={getEventWhitelabelUrl(selectedEvent.id)}
                                eventTitle={selectedEvent.title}
                                brandColor={currentUser?.brandColor || '#72c02c'}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Inscritos */}
            <RegistrationsModal 
                isOpen={regsModalOpen}
                onClose={handleCloseRegsModal}
                event={selectedEvent}
                organizerId={userId}
            />
        </div>
    );
};

export default ManageEvents;
